import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import './Events.css';

const Events = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const tiktokScriptLoaded = useRef(false);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/events`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setEvents(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching events:', err);
            setError('Failed to load events. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        // Load TikTok embed script only once
        if (!tiktokScriptLoaded.current) {
            const script = document.createElement('script');
            script.src = 'https://www.tiktok.com/embed.js';
            script.async = true;
            document.body.appendChild(script);
            tiktokScriptLoaded.current = true;

            script.onload = () => {
                // Force TikTok to process embeds after script loads
                if (window.tiktok && window.tiktok.embedController) {
                    window.tiktok.embedController.process();
                }
            };
        } else if (window.tiktok && window.tiktok.embedController) {
            // If script already loaded, just process embeds
            setTimeout(() => {
                window.tiktok.embedController.process();
            }, 100);
        }
    }, [events]);


    if (loading) {
        return (
            <div className="events-page">
                <div className="container">
                    <div className="loading">Loading events...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="events-page">
                <div className="container">
                    <div className="error">{error}</div>
                    <button onClick={fetchEvents} className="retry-btn">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="events-page">
            <div className="container">
                <header className="events-header">
                    <h1>Events & Highlights</h1>
                    <p>Watch our latest videos showcasing techniques, transformations, and more!</p>
                </header>

                <div className="events-grid">
                    {events.map(event => (
                        <article key={event.id} className="event-card">
                            <div className="video-wrapper">
                                <div className="video-placeholder tiktok">
                                    <div className="platform-badge">
                                        <svg viewBox="0 0 24 24" width="20" height="20">
                                            <path fill="currentColor" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                                        </svg>
                                        TikTok
                                    </div>
                                    <blockquote
                                        className="tiktok-embed"
                                        cite={event.embedUrl}
                                        data-video-id={event.videoId}
                                        style={{ maxWidth: '605px', minWidth: '325px' }}
                                    >
                                        <section>
                                            <a target="_blank" href={event.embedUrl}>
                                                Watch on TikTok
                                            </a>
                                        </section>
                                    </blockquote>
                                </div>

                                {/* Fallback link */}
                                <div className="video-fallback">
                                    <p>Can't see the video?</p>
                                    <a
                                        href={event.embedUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="watch-link"
                                    >
                                        Watch on TikTok
                                    </a>
                                </div>
                            </div>

                            <div className="event-content">
                                <h3>{event.title}</h3>
                                <div className="event-meta">
                                    <span className="event-date">{new Date(event.date).toLocaleDateString()}</span>
                                </div>
                                <p className="event-description">{event.description}</p>
                                <div className="event-category">
                                    <span className="category-tag">
                                        {event.category}
                                    </span>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>

                {events.length === 0 && (
                    <div className="no-events">
                        <p>No events are available right now.</p>
                    </div>
                )}

                <div className="events-cta">
                    <h2>Follow Us on TikTok! And Instagram </h2>
                    <p>Don't miss our latest videos and updates</p>
                    <div className="social-buttons">
                        <a
                            href="https://www.tiktok.com/@takumi.tension"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-btn tiktok"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20">
                                <path fill="currentColor" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
                            </svg>
                            Follow @takumi.tension on TikTok
                        </a>
                        <a
                            href="https://www.instagram.com/takumi_tension"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-btn instagram"
                        >
                            <svg viewBox="0 0 24 24" width="20" height="20">
                                <path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                            Follow @takumi_tension on Instagram
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Events;
